import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { ChevronDown, Check } from 'lucide-react'

/* ==========================================================================
   Select Component
   Based on design.md specifications
   Uses Headless UI Listbox for accessibility
   Task F30: Updated to use pharmacy colors for focus states
   ========================================================================== */

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps {
  /** Options to display in the dropdown */
  options: SelectOption[]
  /** Currently selected value */
  value: string | null
  /** Callback when selection changes */
  onChange: (value: string) => void
  /** Placeholder text when no selection */
  placeholder?: string
  /** Disable the select */
  disabled?: boolean
  /** Error state styling */
  error?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Select dropdown component with keyboard navigation and custom styling.
 * Uses Headless UI Listbox for full accessibility support.
 *
 * @example
 * ```tsx
 * const [value, setValue] = useState<string | null>(null)
 *
 * <Select
 *   options={[
 *     { value: 'ny', label: 'New York' },
 *     { value: 'ca', label: 'California' },
 *   ]}
 *   value={value}
 *   onChange={setValue}
 *   placeholder="Select a state"
 * />
 * ```
 */
export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  error = false,
  className = '',
}: SelectProps) {
  // Find selected option (handles both null and empty string as "no selection")
  const selectedOption = value ? options.find((opt) => opt.value === value) : null

  const buttonBaseStyles = [
    // Layout
    'w-full',
    'px-3 py-3',
    'pr-10',
    // Typography
    'text-base',
    'font-sans',
    'text-left',
    // Background and border
    'bg-white',
    'border',
    'rounded-md',
    // Transitions
    'transition-[border-color,box-shadow] duration-150 ease-in-out',
    // Focus state
    'focus:outline-none',
    // Cursor
    'cursor-pointer',
  ]

  const buttonStateStyles = error
    ? [
        // Error state (not customizable per design.md)
        'border-[var(--color-error)]',
        'focus:border-[var(--color-error)]',
        'focus:shadow-[0_0_0_3px_rgba(198,40,40,0.2)]',
      ]
    : [
        // Default state - uses pharmacy colors for white-labeling (Task F30)
        'border-[var(--color-gray-300)]',
        'focus:border-[var(--pharmacy-primary)]',
        'focus:shadow-[0_0_0_3px_var(--pharmacy-focus-ring)]',
      ]

  const buttonDisabledStyles = disabled
    ? [
        'bg-[var(--color-gray-100)]',
        'text-[var(--color-gray-500)]',
        'cursor-not-allowed',
      ]
    : []

  const buttonClasses = [
    ...buttonBaseStyles,
    ...buttonStateStyles,
    ...buttonDisabledStyles,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  // Use empty string as sentinel for "no selection" to keep component controlled
  // This avoids the "uncontrolled to controlled" React warning
  const controlledValue = value ?? ''

  return (
    <Listbox value={controlledValue} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className="relative">
          <ListboxButton
            className={buttonClasses}
            aria-invalid={error || undefined}
          >
            <span
              className={`block truncate whitespace-normal line-clamp-1 ${!selectedOption ? 'text-[var(--color-gray-500)]' : 'text-[var(--color-charcoal)]'}`}
              title={selectedOption?.label ?? placeholder}
            >
              {selectedOption?.label ?? placeholder}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDown
                className={`h-5 w-5 text-[var(--color-gray-500)] transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <ListboxOptions
            transition
            className="absolute z-10 mt-1 max-h-60 min-w-full w-max max-w-[min(400px,90vw)] overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95 data-[enter]:duration-150 data-[leave]:duration-100"
          >
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="group relative cursor-pointer select-none py-2.5 pl-10 pr-4 text-[var(--color-charcoal)] data-[focus]:bg-[var(--color-soft-lavender)] data-[selected]:bg-[var(--color-soft-lavender)] data-[disabled]:cursor-not-allowed data-[disabled]:text-[var(--color-gray-400)]"
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block ${selected ? 'font-medium' : 'font-normal'}`}
                    >
                      {option.label}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[var(--pharmacy-primary)]">
                        <Check className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      )}
    </Listbox>
  )
}

export default Select
